import { ChevronLeft, ChevronRight, Maximize2, Minimize2, PanelRight, X } from "lucide-react";
import type { ComponentProps, ReactNode, Ref } from "react";

import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

/*
 * The session pane as markup. SessionPaneFrame owns the overlay, the
 * resizable split, the width preset and the published tabs, and renders the
 * pane through these, so a fixture pane (a workflow, a document) composes the
 * same top row, body and floating controls.
 */

/** The pane's width presets; the values of SessionPaneMode (session-pane-layout). */
type PaneMode = "compact-chat" | "balanced" | "full";

/** A tab in the pane's strip. */
export type SessionPaneTabItem = {
  id: string;
  kind: string;
  title: string;
  subtitle?: string;
};

/** The pane: its top row, its body, then the controls floating over its foot. */
export function SessionPaneView({
  "aria-modal": modal,
  "aria-labelledby": labelledBy,
  "aria-describedby": describedBy,
  "data-testid": testId,
  onClick,
  top,
  controls,
  children,
}: Pick<
  ComponentProps<"section">,
  "aria-modal" | "aria-labelledby" | "aria-describedby" | "onClick"
> & {
  "data-testid"?: string;
  /** The tab strip, or the pane's heading when it has no tabs. */
  top: ReactNode;
  /** The floating controls. */
  controls: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section
      role="dialog"
      aria-modal={modal}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      data-mlx-floating-panel-scroll-room="true"
      data-testid={testId}
      className="relative ml-auto flex h-full w-full min-w-[min(360px,100vw)] flex-col overflow-hidden bg-background text-sm shadow-[-18px_0_42px_rgba(15,23,42,0.14)]"
      onClick={onClick}
    >
      <div className="flex h-10 shrink-0 items-center border-b border-border/60">
        {top}
      </div>
      {children}
      {controls}
    </section>
  );
}

/** The top row's label when the pane has no tabs. */
export function SessionPaneHeading({ children }: { children: ReactNode }) {
  return (
    <span className="min-w-0 flex-1 truncate px-3 font-medium">{children}</span>
  );
}

/**
 * The pane's tab strip: every tab in creation order, the active one
 * highlighted, and a scroll button at each end that overflows.
 */
export function SessionPaneTabStripView({
  tabs,
  activeId,
  onSelect,
  listRef,
  edges = { left: false, right: false },
  onScrollTabs,
  className,
}: {
  tabs: readonly SessionPaneTabItem[];
  activeId: string | null;
  onSelect?: (id: string) => void;
  listRef?: Ref<HTMLDivElement>;
  /** The ends of the strip whose tabs are scrolled out of view. */
  edges?: { left: boolean; right: boolean };
  /** Scrolls the strip one step: -1 towards the start, 1 towards the end. */
  onScrollTabs?: (direction: number) => void;
  className?: string;
}) {
  return (
    <Tabs
      value={activeId}
      onValueChange={
        onSelect &&
        ((value) => {
          if (typeof value === "string") onSelect(value);
        })
      }
      className={cn("shrink-0 gap-0", className)}
      data-testid="session-pane-tab-strip"
    >
      <div className="relative flex h-10 min-w-0 items-center bg-background">
      <TabsList ref={listRef} aria-label="Session pane" variant="line" className="h-10 min-w-0 flex-1 justify-start overflow-hidden rounded-none px-3 py-1.5 data-[orientation=horizontal]:h-10">
        {tabs.map((tab) => (
          <TabsTrigger
            key={tab.id}
            value={tab.id}
            title={tab.subtitle ? `${tab.title} · ${tab.subtitle}` : tab.title}
            className="h-7 max-w-[16rem] flex-none rounded-md border-border/50 bg-surface/60 px-2 text-base font-medium text-muted-foreground shadow-none after:hidden hover:bg-surface/80 hover:text-foreground data-active:border-primary/20 data-active:bg-primary/10 data-active:font-semibold data-active:text-primary group-data-[variant=line]/tabs-list:bg-surface/60 group-data-[variant=line]/tabs-list:data-active:bg-primary/10"
            data-pane-tab-kind={tab.kind}
          >
            <span className="truncate">{tab.title}</span>
          </TabsTrigger>
        ))}
      </TabsList>
      {edges.left && <div className="pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center bg-gradient-to-r from-background via-background/95 to-transparent pl-1">
        <Button className="pointer-events-auto size-6 rounded-full border border-border/60 bg-background/90 shadow-sm" variant="ghost" size="icon-sm" aria-label="Scroll tabs left" onClick={onScrollTabs && (() => onScrollTabs(-1))}><ChevronLeft className="size-3.5" /></Button>
      </div>}
      {edges.right && <div className="pointer-events-none absolute inset-y-0 right-0 flex w-10 items-center justify-end bg-gradient-to-l from-background via-background/95 to-transparent pr-1">
        <Button className="pointer-events-auto size-6 rounded-full border border-border/60 bg-background/90 shadow-sm" variant="ghost" size="icon-sm" aria-label="Scroll tabs right" onClick={onScrollTabs && (() => onScrollTabs(1))}><ChevronRight className="size-3.5" /></Button>
      </div>}
      </div>
    </Tabs>
  );
}

/**
 * The controls floating over the pane's foot: its title and status, the
 * width presets, the pane's own actions, then Close.
 */
export function SessionPaneFloatingControls({
  footerInset = 0,
  title,
  subtitle,
  mode,
  onModeChange,
  titleId,
  descriptionId,
  actions,
  closeLabel,
  onClose,
}: {
  /** Space occupied by fixed sheet tabs below the floating toolbar. */
  footerInset?: number;
  title: string;
  subtitle?: string;
  mode: PaneMode;
  onModeChange?: (mode: PaneMode) => void;
  /** The title's id, which names the pane. */
  titleId?: string;
  /** The subtitle's id, which describes the pane. */
  descriptionId?: string;
  /** The pane's own buttons, between the presets and Close. */
  actions?: ReactNode;
  closeLabel: string;
  onClose?: () => void;
}) {
  return (
    <div className="pointer-events-none absolute inset-x-0 z-20 isolate flex justify-center px-4" style={{ bottom: 16 + footerInset }}>
      {/* One row wherever it fits. On a phone, where a pane's actions are
          wider than the screen, they wrap onto lines of their own,
          right-aligned, with the title leading the first line, instead of
          running over one another. */}
      <div className="pointer-events-auto relative flex max-w-full flex-wrap items-center justify-end gap-2 rounded-xl border border-border/60 bg-background px-2.5 py-1.5 shadow-[0_12px_36px_rgba(15,23,42,0.18),0_24px_72px_rgba(15,23,42,0.10)] dark:bg-muted">
      {/* Sized by its content: a pane without a subtitle gets a compact pill
          instead of a fixed-width bar with a blank second line. */}
      <div className="mr-auto min-w-0 max-w-[18rem] px-1">
        <h2 id={titleId} className="truncate text-sm font-semibold leading-5 text-foreground">{title}</h2>
        {subtitle ? (
          <p id={descriptionId} className="truncate text-xs leading-4 text-muted-foreground">{subtitle}</p>
        ) : null}
      </div>
      <SessionPaneModeControls mode={mode} onModeChange={onModeChange} />
      {actions}
      <Button type="button" variant="ghost" size="icon-sm" aria-label={closeLabel} onClick={onClose}>
        <X className="size-4" />
      </Button>
      </div>
    </div>
  );
}

/** The width presets, shown from `lg`: a compact chat, a balanced split, or the pane alone. */
export function SessionPaneModeControls({
  mode,
  onModeChange,
}: {
  mode: PaneMode;
  onModeChange?: (mode: PaneMode) => void;
}) {
  const items: Array<{ mode: PaneMode; label: string; icon: typeof Minimize2 }> = [
    { mode: "compact-chat", label: "Compact chat panel", icon: Minimize2 },
    { mode: "balanced", label: "Balanced split", icon: PanelRight },
    { mode: "full", label: "Full workspace panel", icon: Maximize2 },
  ];
  return (
    <div className="hidden items-center rounded-md border border-border bg-muted/30 p-0.5 lg:flex">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <Button
            key={item.mode}
            type="button"
            size="icon-xs"
            variant={mode === item.mode ? "secondary" : "ghost"}
            aria-label={item.label}
            aria-pressed={mode === item.mode}
            title={item.label}
            className={cn(
              "size-7 rounded-[5px]",
              mode === item.mode ? "shadow-sm" : "text-muted-foreground",
            )}
            onClick={onModeChange && (() => onModeChange(item.mode))}
          >
            <Icon className="size-3.5" />
          </Button>
        );
      })}
    </div>
  );
}
